import { queryOptions } from '@tanstack/react-query';

import {
	getKeywordById,
	getKeywordProducts,
	getKeywords,
	getKeywordsListing,
} from '../api/keywords';
import {
	getEolReplacementRecords,
	getInventory,
	getPrices,
	getUnassignedSkus,
} from '../api/management';
import type { ProductKey } from '../models/product';

export const productQueryKeys = {
	all: ['product'],
	inventory: (itemId: ProductKey) => ['product', itemId, 'inventory'],
	prices: (itemId: ProductKey) => ['product', itemId, 'prices'],
	eolReplacements: (itemId: ProductKey) => ['product', itemId, 'eol-replacements'],
	keywords: ['products', 'keywords'],
	keywordProducts: (keywordId: number) => ['products', 'keywords', keywordId],
	keywordDetails: (keywordId: number) => ['products', 'keywords', keywordId, 'details'],
	unassignedSkus: ['products', 'unassigned-skus'],
	verifyIds: ['products', 'verify-ids'],
} as const;

export const productQueryOptions = {
	inventory: (itemId: ProductKey, enabled: boolean) =>
		queryOptions({
			queryKey: productQueryKeys.inventory(itemId),
			queryFn: () => getInventory(itemId),
			staleTime: 15 * 60 * 1000,
			enabled,
		}),
	prices: (itemId: ProductKey, enabled: boolean) =>
		queryOptions({
			queryKey: productQueryKeys.prices(itemId),
			queryFn: () => getPrices(itemId),
			staleTime: 15 * 60 * 1000,
			enabled,
		}),
	eolReplacements: (itemId: ProductKey, enabled: boolean) =>
		queryOptions({
			queryKey: productQueryKeys.eolReplacements(itemId),
			queryFn: () => getEolReplacementRecords(itemId),
			staleTime: 15 * 60 * 1000,
			enabled,
		}),
	keywords: queryOptions({
		queryKey: productQueryKeys.keywords,
		queryFn: getKeywords,
		staleTime: 5 * 60 * 1000, // 5 minutes
	}),
	keywordListing: queryOptions({
		queryKey: productQueryKeys.keywords,
		queryFn: getKeywordsListing,
		staleTime: 5 * 60 * 1000, // 5 minutes
	}),
	keyword: (keywordId: number) =>
		queryOptions({
			queryKey: productQueryKeys.keywordDetails(keywordId),
			queryFn: () => getKeywordById(keywordId),
			staleTime: 15 * 60 * 1000,
		}),
	keywordProducts: (keywordId: number) =>
		queryOptions({
			queryKey: productQueryKeys.keywordProducts(keywordId),
			queryFn: () => getKeywordProducts(keywordId),
			staleTime: 15 * 60 * 1000,
		}),
	unassignedSkus: (enabled = true) =>
		queryOptions({
			queryKey: productQueryKeys.unassignedSkus,
			queryFn: () => getUnassignedSkus(),
			staleTime: 15 * 60 * 1000,
			enabled,
		}),
};
